import { Component, OnInit } from '@angular/core';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { currency } from 'app/constants/currency.constants';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { DataService, HelperService, HTTPService, DateService } from 'app/services';
import { datetimeFormat } from 'app/constants/date-time-format';
import { EditCommissionDetailsComponent } from '../edit-commission-details/edit-commission-details.component';

import { orderBy, sortBy } from 'lodash';

@Component({
  selector: 'app-commission-details',
  templateUrl: './commission-details.component.html',
  styleUrls: ['./commission-details.component.scss'],
  providers: [DialogService],
})
export class CommissionDetailsComponent implements OnInit {
  orderId: string = '';
  usd: string = currency.USD;
  stats: any[] = [];
  invoiceMetrics: any[] = [];
  serviceMetrics: any[] = [];
  activityLogs: any[] = [];
  isSyncing: boolean = false;
  canViewEditCommission : boolean = false;
  orderCode: string = '';

  constructor(
    private httpService: HTTPService,
    private helperService: HelperService,
    private dialogService: DialogService,
    private dialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private dataService: DataService,
    public dateService: DateService,
  ) {}

  ngOnInit() {
    this.orderId = this.dialogConfig.data.orderId;
    this.orderCode = this.dialogConfig.data.orderCode;
    this.canViewEditCommission  = this.dataService.canViewEditCommission;

    if (this.orderId) {
      this.getCommissionDetails();
    }
  }

  getCommissionDetails() {
    this.httpService.getCommissionDetails(this.orderId).then(
      (data: any) => {
        const { activityLogs, commissionDetails } = data;

        const serviceMetrics = {};

        this.stats = commissionDetails.stats;

        this.invoiceMetrics = [];
        this.serviceMetrics = [];
        this.activityLogs = activityLogs;

        if (commissionDetails.commissions && commissionDetails.commissions.length) {
          commissionDetails.commissions.forEach((commission: any) => {
            if (commission.employee) {
              const { firstName = '', lastName = '' } = commission.employee;

              commission.employee.fullName = `${firstName} ${lastName}`;
              commission.employee.displayName = `${firstName} ${lastName.slice(0, 1)}.`;
            }

            if (commission._id && commission._id.serviceId) {
              const groupId = `${commission._id.serviceId}`;

              if (!serviceMetrics[groupId]) {
                serviceMetrics[groupId] = [];
              }

              if (commission._id.role !== 'tech') {
                serviceMetrics[groupId].unshift(commission);
              } else {
                serviceMetrics[groupId].push(commission);
              }
            } else {
              this.invoiceMetrics.push(commission);
            }
          });
        }

        if (this.stats) {
          this.stats.forEach((stat: any) => {
            stat.hasServiceWriterRole = stat.role.includes('serviceWriter');
            stat.role = sortBy(stat.role);

            if (stat.employee) {
              const { firstName = '', lastName = '' } = stat.employee;

              stat.employee.fullName = `${firstName} ${lastName}`;
              stat.employee.displayName = `${firstName} ${lastName.slice(0, 1)}.`;
            }
          });
          this.stats = orderBy(this.stats, 'hasServiceWriterRole', 'desc');
        }

        this.serviceMetrics = Object.values(serviceMetrics);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  syncCommission() {
    if (this.isSyncing) return;

    this.isSyncing = true;
    this.httpService
      .syncCommissionDetails(this.orderId)
      .then(
        () => {
          this.getCommissionDetails();
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isSyncing = false;
      });
  }

  handleEditEmployeeCommission({ id, firstName, lastName, role }, serviceId: string = 'invoiceMetric') {
    const ref = this.dialogService.open(EditCommissionDetailsComponent, {
      width: '1200px',
      styleClass: 'add-dialog edit-commission-detail-dialog',
      showHeader: false,
      data: {
        serviceId,
        employeeId: id,
        employeeName: `${firstName} ${lastName}`,
        employeeRole: role,
        orderId: this.orderId,
        orderCode: this.orderCode,
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.getCommissionDetails();
      }

      onClose.unsubscribe();
    });
  }

  handleClose(isSuccess: boolean = false, data: any = null) {
    this.dynamicDialogRef.close({ isSuccess, data });
  }

  handleConfirmSync() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '40%',
      styleClass: 'end-session-dialog small-heading-dialog',
      showHeader: false,
      data: {
        isHTML: true,
        headerIcon: 'fas fa-exclamation-triangle flex-center-center font-primary header-icon-primary',
        styleClassHeading: 'font-14',
        title: ``,
        message: `<p class="font-16 font-bold mb-2">Sync commissions?</p><p class="mb-0 font-gray-55">Syncing will override Manual commissions.</p>`,
        button: {
          success: {
            title: 'Update',
          },
          cancel: {
            title: 'Cancel',
          },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.syncCommission();
      }

      onClose.unsubscribe();
    });
  }

  getTime(time) {
    return this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(time).getTime()), datetimeFormat.mmmdyyhhmmaa);
  }
}
